import { useCallback, useEffect, useMemo, useState } from 'react';
import EvalPrintPage from './EvalPrintPage';
import PrintButton from './PrintButton';
import { formatDateFr } from './consignes';

const STORAGE_KEY = 'groupesPrintData';
// État affiché par la barre (chaque instance garde ses propres réglages).
const REGLAGES_KEY = 'groupesPrintReglages';
const REGLAGES_DEFAUT = { mode: 'confort', reponse: 'qcm', ajuste: false };

const lire = (cle) => {
  try {
    const raw = sessionStorage.getItem(cle);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// Chaque instance du moteur commence sur une nouvelle feuille.
const NOUVELLE_FEUILLE = { breakBefore: 'page', pageBreakBefore: 'always' };
// Sujets et leurs élèves : quel élève reçoit quel sujet, combien de copies.
const RecapSujets = ({ sujets }) => sujets.map((s) => (
  // div et non section : index.css centre toute <section> en flex.
  <div key={s.titre} className="mb-5 break-inside-avoid">
    <h2 className="mb-1 text-base font-bold">
      {s.titre} · {s.eleves.length} élève{s.eleves.length > 1 ? 's' : ''}
      <span className="font-normal text-gray-600"> · {s.eleves.length} copie{s.eleves.length > 1 ? 's' : ''}</span>
    </h2>
    <ul className="columns-2 gap-6 text-sm">
      {s.eleves.map((nom, i) => <li key={`${i}-${nom}`}>{nom}</li>)}
    </ul>
  </div>
));
const CORRIGE = { sujet: false, corrige: true, aides: false };
const SUJET = { sujet: true, corrige: false, aides: true };

/**
 * Route /print-groupes — groupes de besoin en papier (F7), un seul document
 * dans l'ordre de la distribution (2026-10-04, épreuve de Musk : 4 boutons et
 * 4 PDF → 1, remplace /print-recap-groupes) : récapitulatif prof, puis chaque sujet suivi de sa planche de
 * coups de pouce, puis les corrigés. Chaque sujet est paginé par le moteur de
 * /print-eval, une instance par sujet (mode intégré) ; les corrigés aussi.
 * Données : sessionStorage['groupesPrintData'] = { meta, classe, sujets:
 * [{ titre, eleves, exercices, coupsDePouce }] }, posé par LaunchModal.
 */
const GroupesPrintPage = () => {
  const [doc] = useState(() => lire(STORAGE_KEY));
  const sujets = useMemo(() => doc?.sujets || [], [doc]);
  // Une instance par sujet et une par corrigé : « Imprimer » attend les deux,
  // et de nouveau après chaque réglage (pagination relancée).
  const [prets, setPrets] = useState(() => new Set());
  const marquerPret = useCallback((cle, ok) => setPrets((p) => {
    if (p.has(cle) === ok) return p;
    const n = new Set(p);
    if (ok) n.add(cle); else n.delete(cle);
    return n;
  }), []);
  const rappels = useMemo(() => Object.fromEntries(sujets.flatMap((s, i) => [
    [`s${i}`, (ok) => marquerPret(`s${i}`, ok)],
    [`c${i}`, (ok) => marquerPret(`c${i}`, ok)],
  ])), [sujets, marquerPret]);

  // Barre de réglages commune : chaque clic est diffusé à toutes les instances.
  const [reglages, setReglages] = useState(() => ({ ...REGLAGES_DEFAUT, ...lire(REGLAGES_KEY) }));
  const [ordre, setOrdre] = useState(null);
  useEffect(() => {
    try { sessionStorage.setItem(REGLAGES_KEY, JSON.stringify(reglages)); } catch { /* quota */ }
  }, [reglages]);
  const diffuser = (type, valeur, maj = {}) => {
    setOrdre((o) => ({ n: (o?.n || 0) + 1, type, valeur }));
    setReglages((r) => (type === 'reset' ? REGLAGES_DEFAUT : { ...r, ajuste: true, ...maj }));
  };
  const donnees = useMemo(() => sujets.map((s) => {
    const meta = { ...doc.meta, titre: `${doc.meta.titre} · ${s.titre}` };
    const selectedQuestions = s.exercices.map((q, i) => ({ ...q, __position: i + 1 }));
    return {
      sujet: { selectedQuestions, meta: { ...meta, coupsDePouce: s.coupsDePouce }, contexte: null },
      corrige: { selectedQuestions, meta: { ...meta, coupsDePouce: false }, contexte: null },
    };
  }), [doc, sujets]);
  const toutPret = sujets.length > 0 && prets.size === sujets.length * 2;

  useEffect(() => {
    if (!doc?.meta) return undefined;
    document.title = ['Eval', doc.meta.titre, doc.classe, doc.meta.date].filter(Boolean).join('_').replace(/[\\/:*?"<>|]+/g, '-');
    return () => { document.title = 'TanoEd'; };
  }, [doc]);

  if (sujets.length === 0) {
    return <p className="p-8 text-sm text-gray-500">Aucun sujet à imprimer : ouvrez ce document depuis « Générer l'éval papier ».</p>;
  }

  return (
    <div className="min-h-screen w-full flex-1 bg-white">
      {/* Le fond gris du body s'imprimerait sous la dernière page. */}
      <style>{'@media print { body { background: white !important; } }'}</style>
      {/* ep-root pour les couleurs de la barre, sans sa hauteur ni sa marge. */}
      <div className="ep-root" style={{ minHeight: 0, padding: 0 }}>
        <PrintButton
          mode={reglages.mode}
          onToggleMode={() => { const m = reglages.mode === 'compact' ? 'confort' : 'compact'; diffuser('mode', m, { mode: m }); }}
          onGlobalZoom={(f) => diffuser('zoom', f)}
          onGlobalHalf={() => diffuser('demi')}
          onGlobalWidthAuto={() => diffuser('auto')}
          reponseMode={reglages.reponse}
          onGlobalReponse={(m) => diffuser('reponse', m, { reponse: m })}
          showReset={reglages.ajuste}
          onReset={() => diffuser('reset')}
          notice={toutPret ? null : 'Préparation des sujets…'}
        />
      </div>
      <main className="mx-auto max-w-3xl p-8 text-gray-900">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Réservé au professeur · à garder</p>
        <h1 className="text-xl font-bold">Récapitulatif des sujets · {doc.meta.titre}</h1>
        <p className="mb-6 text-sm text-gray-600">{[doc.classe, formatDateFr(doc.meta.date)].filter(Boolean).join(' · ')}</p>
        <RecapSujets sujets={sujets} />
        <p className="text-sm text-gray-600">
          Ensuite : {sujets.map((s) => s.titre).join(', ')} (à photocopier), puis les corrigés.
        </p>
      </main>

      {donnees.map((d, i) => (
        <div key={`s${i}`} style={NOUVELLE_FEUILLE}>
          <EvalPrintPage donnees={d.sujet} cleReglages={`:groupes:s${i}`} parties={SUJET} onPret={rappels[`s${i}`]} ordre={ordre} />
        </div>
      ))}
      {donnees.map((d, i) => (
        <div key={`c${i}`} style={NOUVELLE_FEUILLE}>
          <EvalPrintPage donnees={d.corrige} cleReglages={`:groupes:c${i}`} parties={CORRIGE} onPret={rappels[`c${i}`]} ordre={ordre} />
        </div>
      ))}
    </div>
  );
};

export default GroupesPrintPage;
